import React from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import {Smartphone, Target, Play, MessageCircleDashed as MessageCircle, TrendingUp} from 'lucide-react'

const HowItWorks = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const steps = [
    {
      number: '1',
      icon: Smartphone,
      title: 'Acesse a plataforma',
      description: 'Pelo celular ou computador',
      color: 'from-blue-500 to-cyan-500',
    },
    {
      number: '2',
      icon: Target,
      title: 'Escolha seu objetivo',
      description: 'Nível ou área de estudo',
      color: 'from-purple-500 to-pink-500',
    },
    {
      number: '3',
      icon: Play,
      title: 'Assista no seu ritmo',
      description: 'Sem horários engessados',
      color: 'from-orange-500 to-red-500',
    },
    {
      number: '4',
      icon: MessageCircle,
      title: 'Tire suas dúvidas',
      description: 'Com Lucy IA ou professores',
      color: 'from-green-500 to-emerald-500',
    },
    {
      number: '5',
      icon: TrendingUp,
      title: 'Mantenha constância',
      description: 'Com apoio real',
      color: 'from-yellow-500 to-orange-500',
    },
  ]

  return (
    <section ref={ref} className="py-20 md:py-32 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 mb-6 leading-tight">
              Como o EW Pass{' '}
              <span className="bg-gradient-to-r from-[#0D7FA3] to-[#C64DAD] bg-clip-text text-transparent">
                entra na sua rotina
              </span>
            </h2>
          </motion.div>

          {/* Steps - Desktop: Horizontal with connecting lines */}
          <div className="hidden md:block mb-16">
            <div className="relative">
              {/* Connection line */}
              <div className="absolute top-20 left-0 right-0 h-1 bg-gradient-to-r from-[#0D7FA3] via-[#C64DAD] to-[#0D7FA3] opacity-20"></div>

              <div className="grid grid-cols-5 gap-4">
                {steps.map((step, index) => {
                  const Icon = step.icon
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 50 }}
                      animate={inView ? { opacity: 1, y: 0 } : {}}
                      transition={{ duration: 0.6, delay: index * 0.15 }}
                      className="relative"
                    >
                      <div className="flex flex-col items-center text-center">
                        {/* Number badge */}
                        <motion.div
                          whileHover={{ scale: 1.1, rotate: 5 }}
                          className={`w-16 h-16 bg-gradient-to-br ${step.color} rounded-full flex items-center justify-center text-white font-black text-2xl mb-4 shadow-lg relative z-10`}
                        >
                          {step.number}
                        </motion.div>

                        {/* Icon */}
                        <div className="w-20 h-20 bg-white rounded-2xl shadow-lg flex items-center justify-center mb-4 border-2 border-gray-100 hover:border-[#0D7FA3] transition-colors duration-300">
                          <Icon className="w-10 h-10 text-gray-700" strokeWidth={1.5} />
                        </div>

                        {/* Content */}
                        <h3 className="text-lg font-bold text-gray-900 mb-2">
                          {step.title}
                        </h3>
                        <p className="text-sm text-gray-600 leading-relaxed">
                          {step.description}
                        </p>
                      </div>
                    </motion.div>
                  )
                })}
              </div>
            </div>
          </div>

          {/* Steps - Mobile: Vertical */}
          <div className="md:hidden space-y-6 mb-12">
            {steps.map((step, index) => {
              const Icon = step.icon
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -30 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex gap-4 items-start"
                >
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    className={`w-12 h-12 bg-gradient-to-br ${step.color} rounded-full flex items-center justify-center text-white font-black text-xl shadow-lg flex-shrink-0`}
                  >
                    {step.number}
                  </motion.div>

                  <div className="flex-1 bg-white rounded-2xl p-5 shadow-md border-2 border-gray-100 hover:border-[#0D7FA3] transition-colors duration-300">
                    <div className="flex items-center gap-3 mb-2">
                      <Icon className="w-6 h-6 text-gray-700" strokeWidth={2} />
                      <h3 className="text-lg font-bold text-gray-900">
                        {step.title}
                      </h3>
                    </div>
                    <p className="text-gray-600">
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              )
            })}
          </div>

          {/* Bottom highlights */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="grid md:grid-cols-3 gap-6"
          >
            {[
              { emoji: '⏰', text: 'Sem horários engessados' },
              { emoji: '🤝', text: 'Sem estudar sozinho' },
              { emoji: '✨', text: 'Sem pressão desnecessária' },
            ].map((item, index) => (
              <motion.div
                key={index}
                whileHover={{ scale: 1.05, y: -5 }}
                className="bg-gradient-to-br from-[#0D7FA3]/10 to-[#C64DAD]/10 rounded-2xl p-6 text-center border-2 border-[#0D7FA3]/20 hover:border-[#0D7FA3]/40 transition-all duration-300"
              >
                <div className="text-4xl mb-3">{item.emoji}</div>
                <p className="text-lg font-bold text-gray-900">{item.text}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default HowItWorks
